import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch, useApp } from "../../context/AppContext";
import { Button, Modal, DashboardStatCard, HelpTooltip } from "../../components/common/UI";
import DepartmentDropdown from "../../components/common/DepartmentSelect";
import { useDepartments } from "../../hooks/useDepartments";
import {
  Server,
  Database,
  Network,
  Building2,
  Upload,
  PenLine,
  Search,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Trash2,
  FileSpreadsheet,
  Download,
  Loader2,
  Layers,
  HardDrive,
  Plus,
  X,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Copy,
  SlidersHorizontal,
  FileDown,
  CheckSquare,
  Square,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Power,
  PowerOff,
} from "lucide-react";

const premiumTopLine =
  "pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-400/50 to-transparent";

const TABLE_HEAD_GRADIENT =
  "linear-gradient(90deg, #eff6ff 0%, #dbeafe 35%, #bfdbfe 65%, #dbeafe 100%)";

const TABLE_HEAD_CLASS = "text-slate-700";

const TABLE_SCROLL_CLASS = "asset-table-scroll";

const TABLE_SCROLL_CSS = `
  .asset-table-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(59, 130, 246, 0.45) transparent;
  }
  .asset-table-scroll::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }
  .asset-table-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  .asset-table-scroll::-webkit-scrollbar-thumb {
    background: rgba(59, 130, 246, 0.4);
    border-radius: 999px;
  }
  .asset-table-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(37, 99, 235, 0.55);
  }
`;

const inputClass =
  "w-full rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100 shadow-sm";

const EMPTY_FORM = {
  entry_id: "",
  ip_address: "",
  database_name: "",
  department_id: "",
};

const ENTRY_ID_PATTERN = /^[A-Za-z0-9._\-/]+$/;

function parseApiError(data, fallback = "Request failed") {
  const detail = data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
        const msg = item?.msg?.replace(/^Value error,\s*/i, "") || String(item);
        return field ? `${field}: ${msg}` : msg;
      })
      .join("; ");
  }
  return String(detail);
}

function isValidIpAddress(value) {
  const ipv4 =
    /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d?\d)$/;
  if (ipv4.test(value)) return true;
  if (/^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/.test(value)) return true;
  if (/^(([0-9a-fA-F]{0,4}:)+::([0-9a-fA-F]{0,4}:)*[0-9a-fA-F]{0,4})$/.test(value)) {
    return true;
  }
  return false;
}

function normalizeAssetPayload(form, { includeEntryId = false } = {}) {
  const payload = {
    ip_address: form.ip_address.trim(),
    database_name: form.database_name.trim(),
    department_id: String(form.department_id || "").trim(),
  };
  if (includeEntryId) {
    const entryId = form.entry_id?.trim();
    if (entryId) payload.entry_id = entryId;
  }
  return payload;
}

function validateAssetPayload(payload, { requireEntryId = false } = {}) {
  const errors = [];
  if (requireEntryId) {
    if (!payload.entry_id) errors.push("Entry ID is required");
    else if (!ENTRY_ID_PATTERN.test(payload.entry_id)) {
      errors.push(
        "Entry ID may only contain letters, numbers, dots, dashes, underscores, and slashes",
      );
    }
  } else if (payload.entry_id && !ENTRY_ID_PATTERN.test(payload.entry_id)) {
    errors.push(
      "Entry ID may only contain letters, numbers, dots, dashes, underscores, and slashes",
    );
  }
  if (!payload.ip_address) errors.push("IP address is required");
  else if (!isValidIpAddress(payload.ip_address)) {
    errors.push("Enter a valid IPv4 or IPv6 address (e.g. 192.168.1.10)");
  }
  if (!payload.database_name) errors.push("Database name is required");
  if (!payload.department_id) errors.push("Department is required");
  return errors;
}

function normalizeAssetStatus(status) {
  if (status === "inactive") return "inactive";
  return "active";
}

function AssetStatusBadge({ status, onToggle, disabled = false }) {
  const isActive = normalizeAssetStatus(status) === "active";
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      title={isActive ? "Mark as inactive" : "Mark as active"}
      className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase transition disabled:opacity-60 ${
        isActive
          ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200/60 hover:bg-emerald-200/80"
          : "bg-slate-100 text-slate-500 ring-1 ring-slate-200/60 hover:bg-slate-200/80"
      }`}
    >
      {isActive ? "Active" : "Inactive"}
    </button>
  );
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function pollBulkUploadTask(taskId, { onProgress } = {}) {
  for (let i = 0; i < 120; i += 1) {
    const res = await apiFetch(`/asset-tracker/entries/bulk/status/${taskId}`);
    if (!res.ok) throw new Error("Could not check bulk upload status");
    const data = await res.json();
    onProgress?.(data.status);
    if (data.status === "success") return data.result;
    if (data.status === "failed") {
      throw new Error(data.error || "Bulk upload failed");
    }
    await sleep(1500);
  }
  throw new Error("Bulk upload timed out — check back shortly");
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

function normalizeHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function parseCsvText(text) {
  const lines = String(text || "")
    .trim()
    .split(/\r?\n/)
    .filter((line) => line.trim());
  if (!lines.length) return [];

  const splitLine = (line) => {
    const out = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
        continue;
      }
      if (ch === "," && !inQuotes) {
        out.push(cur.trim());
        cur = "";
      } else {
        cur += ch;
      }
    }
    out.push(cur.trim());
    return out;
  };

  const headers = splitLine(lines[0]).map(normalizeHeader);
  const idx = {
    entry_id: headers.findIndex((h) =>
      ["entry_id", "entries_id", "entryid", "id"].includes(h),
    ),
    ip: headers.findIndex((h) => ["ip", "ip_address", "ipaddress"].includes(h)),
    database_name: headers.findIndex((h) =>
      ["database_name", "database", "db_name", "db"].includes(h),
    ),
    department: headers.findIndex((h) =>
      ["department_id", "department", "dept", "department_code"].includes(h),
    ),
  };

  if (idx.ip < 0 || idx.database_name < 0 || idx.department < 0) {
    throw new Error(
      "CSV must include columns: ip, database_name, department_id (entry_id is optional)",
    );
  }

  return lines.slice(1).map((line, rowIndex) => {
    const cols = splitLine(line);
    return {
      row: rowIndex + 2,
      entry_id: idx.entry_id >= 0 ? cols[idx.entry_id] || "" : "",
      ip_address: cols[idx.ip] || "",
      database_name: cols[idx.database_name] || "",
      department_ref: cols[idx.department] || "",
    };
  });
}

function csvEscape(value) {
  const str = String(value ?? "");
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

function exportEntriesToCsv(rows, filename) {
  const header = ["entry_id", "ip_address", "database_name", "department", "source", "status", "submitted"];
  const lines = [header.join(",")];
  rows.forEach((row) => {
    lines.push(
      [
        row.entry_id,
        row.ip_address,
        row.database_name,
        row.department_name || "",
        row.source,
        normalizeAssetStatus(row.status),
        formatDate(row.created_at),
      ]
        .map(csvEscape)
        .join(","),
    );
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function resolveDepartmentId(ref, departments) {
  const value = String(ref || "").trim();
  if (!value) return "";
  const byId = departments.find((d) => d.id === value);
  if (byId) return byId.id;
  const lower = value.toLowerCase();
  const byCode = departments.find(
    (d) => String(d.code || "").toLowerCase() === lower,
  );
  if (byCode) return byCode.id;
  const byName = departments.find(
    (d) => String(d.name || "").toLowerCase() === lower,
  );
  if (byName) return byName.id;
  return "";
}

function PremiumBlueBg() {
  return (
    <div
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(180deg, #f0f7ff 0%, #e8f2ff 45%, #eef4ff 100%)",
      }}
    />
  );
}

function PageIcon({ icon: Icon }) {
  return (
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-md shadow-blue-500/20 flex-shrink-0">
      <Icon size={18} className="text-white" strokeWidth={2.25} />
    </div>
  );
}

function StatCard({ label, value, icon, gradient, tooltip }) {
  return (
    <DashboardStatCard
      label={label}
      value={value}
      icon={icon}
      gradient={gradient}
      tooltip={tooltip}
    />
  );
}

function DatabaseNameField({ value, onChange, databases, loading, required = true }) {
  const options = databases || [];
  const hasOptions = options.length > 0;
  const valueInList = options.some((d) => (d.label || d.name) === value);

  if (!hasOptions && !loading) {
    return (
      <>
        <input
          required={required}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. PostgreSQL · customer_prod_db"
          className={inputClass}
        />
        <p className="text-[11px] text-slate-500 mt-1">
          No Automated ROPA databases yet — enter manually.
        </p>
      </>
    );
  }

  return (
    <>
      <select
        required={required}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
        disabled={loading}
      >
        <option value="">
          {loading ? "Loading databases…" : "Select database from Auto ROPA"}
        </option>
        {!valueInList && value ? (
          <option value={value}>{value} (current)</option>
        ) : null}
        {options.map((d) => {
          const label = d.label || d.name;
          return (
            <option key={label} value={label}>
              {label}
            </option>
          );
        })}
      </select>
      {!loading && hasOptions ? (
        <p className="text-[11px] text-slate-500 mt-1">
          Platform and database stored together in database name
        </p>
      ) : null}
    </>
  );
}

function EditAssetModal({
  open,
  onClose,
  form,
  setField,
  departments,
  departmentsLoading,
  ropaDatabases,
  ropaDatabasesLoading,
  submitting,
  onSubmit,
}) {
  return (
    <Modal open={open} onClose={onClose} bare zIndex={200} align="top" size="lg">
      <div className="w-full max-w-2xl bg-white rounded-[20px] border border-blue-500/10 shadow-2xl shadow-slate-900/15 overflow-hidden animate-slide-up">
        <span className={premiumTopLine} />
        <div className="relative px-5 sm:px-6 pt-5 pb-4 bg-gradient-to-br from-blue-50/90 via-white to-indigo-50/40 border-b border-blue-500/10">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <PageIcon icon={PenLine} />
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
                  Edit asset
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                  Update IP, database, or department
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-xl border border-slate-200 bg-white text-slate-500 flex items-center justify-center hover:bg-slate-50 hover:text-slate-700 transition flex-shrink-0"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="px-5 sm:px-6 py-5">
          <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="block sm:col-span-2">
              <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-1.5">
                <Server size={13} className="text-blue-500" /> Entry ID
                <HelpTooltip content="Assigned when the asset is registered. It cannot be changed." size={12} position="bottom" />
              </span>
              <div className="rounded-xl border border-slate-200/80 bg-slate-50 px-3 py-2.5 text-sm font-mono text-slate-700">
                {form.entry_id || "—"}
              </div>
            </div>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-1.5">
                <Network size={13} className="text-blue-500" /> IP address
                <HelpTooltip content="Network address of the system that stores the data." size={12} position="bottom" />
              </span>
              <input
                required
                value={form.ip_address}
                onChange={(e) => setField("ip_address", e.target.value)}
                className={inputClass}
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-1.5">
                <Database size={13} className="text-blue-500" /> Database name
                <HelpTooltip content="Database or platform name, chosen from Automated ROPA or typed in." size={12} position="bottom" />
              </span>
              <DatabaseNameField
                value={form.database_name}
                onChange={(val) => setField("database_name", val)}
                databases={ropaDatabases}
                loading={ropaDatabasesLoading}
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-1.5">
                <Building2 size={13} className="text-blue-500" /> Department
                <HelpTooltip content="The department that owns this asset." size={12} position="bottom" />
              </span>
              <DepartmentDropdown
                value={form.department_id}
                onChange={(e) => setField("department_id", e.target.value)}
                departments={departments}
                loading={departmentsLoading}
                required
                style={{
                  width: "100%",
                  minWidth: 0,
                  padding: "10px 12px",
                  fontSize: 14,
                  borderRadius: 12,
                }}
              />
            </label>
            <div className="sm:col-span-2 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
              <Button type="button" variant="secondary" onClick={onClose} className="w-full sm:w-auto !rounded-xl">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto !rounded-xl !bg-gradient-to-r !from-blue-600 !to-indigo-600 hover:!from-blue-700 hover:!to-indigo-700 !border-0 !shadow-md !shadow-blue-500/20"
              >
                {submitting ? (
                  <>
                    <Loader2 size={15} className="animate-spin mr-2" /> Saving…
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={15} className="mr-2" /> Save changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}

function RegisterAssetModal({
  open,
  onClose,
  mode,
  setMode,
  form,
  setField,
  departments,
  departmentsLoading,
  ropaDatabases,
  ropaDatabasesLoading,
  submitting,
  onManualSubmit,
  bulkText,
  setBulkText,
  bulkPreview,
  setBulkPreview,
  bulkErrors,
  setBulkErrors,
  onBulkPreview,
  onBulkSubmit,
  onFileUpload,
  downloadTemplate,
  fileRef,
}) {
  return (
    <Modal open={open} onClose={onClose} bare zIndex={200} align="top" size="lg">
      <div className="w-full max-w-2xl bg-white rounded-[20px] border border-blue-500/10 shadow-2xl shadow-slate-900/15 overflow-hidden animate-slide-up">
        <span className={premiumTopLine} />
        <div className="relative px-5 sm:px-6 pt-5 pb-4 bg-gradient-to-br from-blue-50/90 via-white to-indigo-50/40 border-b border-blue-500/10">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <PageIcon icon={HardDrive} />
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
                  Register asset
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                  Add a single entry or import multiple rows via CSV
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-xl border border-slate-200 bg-white text-slate-500 flex items-center justify-center hover:bg-slate-50 hover:text-slate-700 transition flex-shrink-0"
            >
              <X size={16} />
            </button>
          </div>

          <div className="mt-4 flex w-full sm:w-auto rounded-xl bg-slate-100 p-1 gap-1">
            {[
              { id: "manual", label: "Manual entry", icon: PenLine, tip: "Type one asset: IP address, database, and department." },
              { id: "bulk", label: "Bulk CSV", icon: FileSpreadsheet, tip: "Upload or paste many assets from a CSV file." },
            ].map(({ id, label, icon: Icon, tip }) => (
              <button
                key={id}
                type="button"
                title={tip}
                onClick={() => setMode(id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition ${
                  mode === id
                    ? "bg-white text-indigo-700 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-5 sm:px-6 py-5 max-h-[calc(100dvh-12rem)] overflow-y-auto">
          {mode === "manual" ? (
            <form onSubmit={onManualSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 rounded-xl border border-blue-100 bg-blue-50/50 px-3 py-2.5 text-xs text-slate-600">
                Entry ID is assigned automatically when you register an asset (e.g.{" "}
                <span className="font-mono text-slate-700">AST-2026-00001</span>).
              </div>
              <label className="block">
                <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-1.5">
                  <Network size={13} className="text-blue-500" /> IP address
                  <HelpTooltip content="Network address of the system that stores the data." size={12} position="bottom" />
                </span>
                <input
                  required
                  value={form.ip_address}
                  onChange={(e) => setField("ip_address", e.target.value)}
                  placeholder="e.g. 192.168.10.42"
                  className={inputClass}
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-1.5">
                  <Database size={13} className="text-blue-500" /> Database name
                  <HelpTooltip content="Database or platform name, chosen from Automated ROPA or typed in." size={12} position="bottom" />
                </span>
                <DatabaseNameField
                  value={form.database_name}
                  onChange={(val) => setField("database_name", val)}
                  databases={ropaDatabases}
                  loading={ropaDatabasesLoading}
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-1.5">
                  <Building2 size={13} className="text-blue-500" /> Department
                  <HelpTooltip content="The department that owns this asset." size={12} position="bottom" />
                </span>
                <DepartmentDropdown
                  value={form.department_id}
                  onChange={(e) => setField("department_id", e.target.value)}
                  departments={departments}
                  loading={departmentsLoading}
                  required
                  style={{
                    width: "100%",
                    minWidth: 0,
                    padding: "10px 12px",
                    fontSize: 14,
                    borderRadius: 12,
                  }}
                />
              </label>
              <div className="sm:col-span-2 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  className="w-full sm:w-auto !rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto !rounded-xl !bg-gradient-to-r !from-blue-600 !to-indigo-600 hover:!from-blue-700 hover:!to-indigo-700 !border-0 !shadow-md !shadow-blue-500/20"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={15} className="animate-spin mr-2" /> Submitting…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={15} className="mr-2" /> Submit entry
                    </>
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                <Button
                  variant="secondary"
                  title="Download a sample CSV with ip, database_name, and department_id columns."
                  onClick={downloadTemplate}
                  className="w-full sm:w-auto !rounded-xl !text-sm !border-blue-200/60 !bg-blue-50/50 hover:!bg-blue-50"
                >
                  <Download size={14} className="mr-1.5" /> Download template
                </Button>
                <Button
                  variant="secondary"
                  title="Choose a CSV file with ip, database_name, and department_id columns."
                  onClick={() => fileRef.current?.click()}
                  className="w-full sm:w-auto !rounded-xl !text-sm !border-blue-200/60 !bg-blue-50/50 hover:!bg-blue-50"
                >
                  <Upload size={14} className="mr-1.5" /> Choose CSV file
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => onFileUpload(e.target.files?.[0])}
                />
              </div>

              <div
                className="rounded-xl border-2 border-dashed border-blue-200/60 bg-gradient-to-br from-blue-50/40 to-indigo-50/30 p-3"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  onFileUpload(e.dataTransfer.files?.[0]);
                }}
              >
                <p className="text-xs font-semibold text-slate-600 mb-2 inline-flex items-center gap-1">
                  CSV rows
                  <HelpTooltip
                    content="Paste rows or drop a file. Columns are ip, database_name, and department_id. Entry IDs are assigned when you submit."
                    size={12}
                    position="bottom"
                    maxWidth={300}
                  />
                </p>
                <textarea
                  title="Paste CSV rows or drop a file. Columns: ip, database_name, department_id."
                  value={bulkText}
                  onChange={(e) => {
                    setBulkText(e.target.value);
                    setBulkPreview([]);
                    setBulkErrors([]);
                  }}
                  rows={6}
                  placeholder={`Paste CSV or drop a file…\nip,database_name,department_id`}
                  className={`${inputClass} font-mono text-xs sm:text-sm min-h-[120px] !border-blue-100/80`}
                />
              </div>

              {bulkErrors.length > 0 && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs sm:text-sm text-red-700 space-y-1">
                  {bulkErrors.slice(0, 8).map((msg) => (
                    <p key={msg} className="flex items-start gap-2">
                      <AlertCircle size={14} className="mt-0.5 flex-shrink-0" /> {msg}
                    </p>
                  ))}
                </div>
              )}

              {bulkPreview.length > 0 && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-3">
                  <p className="text-xs sm:text-sm font-semibold text-emerald-800 mb-2">
                    Preview — {bulkPreview.length} rows ready
                  </p>
                  <div className="overflow-x-auto -mx-1">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-slate-500">
                          <th className="py-1 pr-3" title="Assigned when these rows are submitted.">Entry ID</th>
                          <th className="py-1 pr-3" title="Network address of the system.">IP</th>
                          <th className="py-1 pr-3" title="Database or platform name.">Database</th>
                          <th className="py-1" title="The department that owns this asset.">Department</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkPreview.slice(0, 5).map((row) => (
                          <tr key={row.row} className="border-t border-emerald-100">
                            <td className="py-1.5 pr-3 font-medium">
                              {row.entry_id?.trim() || (
                                <span className="text-slate-400 italic">Auto</span>
                              )}
                            </td>
                            <td className="py-1.5 pr-3">{row.ip_address}</td>
                            <td className="py-1.5 pr-3">{row.database_name}</td>
                            <td className="py-1.5">
                              {row.department_id ? (
                                <span className="text-emerald-700">Matched</span>
                              ) : (
                                <span className="text-red-600">Not found</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <Button variant="secondary" onClick={onClose} className="w-full sm:w-auto !rounded-xl">
                  Cancel
                </Button>
                <Button variant="secondary" onClick={onBulkPreview} className="w-full sm:w-auto !rounded-xl">
                  Preview rows
                </Button>
                <Button
                  onClick={onBulkSubmit}
                  disabled={submitting}
                  className="w-full sm:w-auto !rounded-xl !bg-gradient-to-r !from-blue-600 !to-indigo-600 hover:!from-blue-700 hover:!to-indigo-700 !border-0 !shadow-md !shadow-blue-500/20"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={15} className="animate-spin mr-2" /> Uploading…
                    </>
                  ) : (
                    <>
                      <Upload size={15} className="mr-2" /> Upload bulk entries
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default function AssetTracker() {
  const { showNotification } = useApp();
  const { departments, loading: departmentsLoading } = useDepartments();
  const fileRef = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [mode, setMode] = useState("manual");
  const [form, setForm] = useState(EMPTY_FORM);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, departments: 0 });
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [bulkPreview, setBulkPreview] = useState([]);
  const [bulkErrors, setBulkErrors] = useState([]);

  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [filterSource, setFilterSource] = useState("all");
  const [filterAssetStatus, setFilterAssetStatus] = useState("all");
  const [filterDept, setFilterDept] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [ropaDatabases, setRopaDatabases] = useState([]);
  const [ropaDatabasesLoading, setRopaDatabasesLoading] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const tag = document.activeElement?.tagName;
      const isTyping = tag === "INPUT" || tag === "TEXTAREA";
      if (e.key === "/" && !isTyping) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      if (search.trim()) params.set("search", search.trim());
      if (filterDept) params.set("department_id", filterDept);
      if (filterSource !== "all") params.set("source", filterSource);
      if (filterAssetStatus !== "all") params.set("asset_status", filterAssetStatus);

      const res = await apiFetch(`/asset-tracker/entries?${params.toString()}`);
      if (!res.ok) throw new Error((await res.json()).detail || "Failed to load");
      const data = await res.json();
      setEntries(data.items || []);
      setTotalEntries(data.total || 0);
      setTotalPages(data.total_pages || 1);
      if (data.stats) setStats(data.stats);
    } catch (e) {
      showNotification(e.message || "Failed to load asset entries", "error");
      setEntries([]);
      setTotalEntries(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    sortBy,
    sortDir,
    search,
    filterDept,
    filterSource,
    filterAssetStatus,
    showNotification,
  ]);

  useEffect(() => {
    const timer = setTimeout(loadEntries, search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [loadEntries, search]);

  const loadRopaDatabases = useCallback(async () => {
    setRopaDatabasesLoading(true);
    try {
      const res = await apiFetch("/ropa/automated/databases");
      if (!res.ok) throw new Error("Failed to load databases");
      const data = await res.json();
      setRopaDatabases(data.databases || []);
    } catch {
      setRopaDatabases([]);
    } finally {
      setRopaDatabasesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (modalOpen || editModalOpen) {
      loadRopaDatabases();
    }
  }, [modalOpen, editModalOpen, loadRopaDatabases]);

  const currentPage = Math.min(page, totalPages);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const hasActiveFilters =
    filterSource !== "all" || filterAssetStatus !== "all" || Boolean(filterDept);

  const clearFilters = () => {
    setFilterSource("all");
    setFilterAssetStatus("all");
    setFilterDept("");
    setPage(1);
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={11} className="opacity-40" />;
    return sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />;
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = entries.every((r) => next.has(r.id));
      entries.forEach((r) => {
        if (allSelected) next.delete(r.id);
        else next.add(r.id);
      });
      return next;
    });
  };

  const handleCopyIp = async (ip) => {
    const ok = await copyToClipboard(ip);
    showNotification(ok ? "IP address copied" : "Could not copy IP address", ok ? "success" : "error");
  };

  const handleToggleStatus = async (row) => {
    const nextStatus =
      normalizeAssetStatus(row.status) === "active" ? "inactive" : "active";
    setStatusUpdatingId(row.id);
    try {
      const res = await apiFetch(`/asset-tracker/entries/${row.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(parseApiError(err, "Could not update status"));
      }
      showNotification(
        `Asset marked ${nextStatus === "active" ? "active" : "inactive"}`,
        "success",
      );
      await loadEntries();
    } catch (err) {
      showNotification(err.message || "Could not update status", "error");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.size) return;
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const results = await Promise.allSettled(
        ids.map((id) => apiFetch(`/asset-tracker/entries/${id}`, { method: "DELETE" })),
      );
      const failed = results.filter((r) => r.status === "rejected" || !r.value?.ok).length;
      const succeeded = ids.length - failed;
      if (succeeded) {
        showNotification(
          `${succeeded} ${succeeded === 1 ? "entry" : "entries"} removed${failed ? `, ${failed} failed` : ""}`,
          failed ? "warning" : "success",
        );
      } else {
        showNotification("Could not delete selected entries", "error");
      }
      setSelectedIds(new Set());
      await loadEntries();
    } finally {
      setBulkDeleting(false);
    }
  };

  const fetchAllForExport = async () => {
    const params = new URLSearchParams({
      page: "1",
      page_size: "500",
      sort_by: sortBy,
      sort_dir: sortDir,
    });
    if (search.trim()) params.set("search", search.trim());
    if (filterDept) params.set("department_id", filterDept);
    if (filterSource !== "all") params.set("source", filterSource);
    if (filterAssetStatus !== "all") params.set("asset_status", filterAssetStatus);
    const res = await apiFetch(`/asset-tracker/entries?${params.toString()}`);
    if (!res.ok) throw new Error("Export failed");
    const data = await res.json();
    return data.items || [];
  };

  const handleExport = async () => {
    try {
      const rows = await fetchAllForExport();
      if (!rows.length) {
        showNotification("No entries to export", "warning");
        return;
      }
      exportEntriesToCsv(rows, `asset_tracker_export_${Date.now()}.csv`);
      showNotification(`Exported ${rows.length} entries`, "success");
    } catch {
      showNotification("Could not export entries", "error");
    }
  };

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setEditField = (key, value) =>
    setEditForm((prev) => ({ ...prev, [key]: value }));

  const closeModal = () => {
    setModalOpen(false);
    setForm(EMPTY_FORM);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingEntryId(null);
    setEditForm(EMPTY_FORM);
  };

  const openModal = (nextMode = "manual") => {
    setForm(EMPTY_FORM);
    setMode(nextMode);
    setModalOpen(true);
  };

  const openEditModal = (row) => {
    setEditingEntryId(row.id);
    setEditForm({
      entry_id: row.entry_id || "",
      ip_address: row.ip_address || "",
      database_name: row.database_name || "",
      department_id: row.department_id || "",
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingEntryId) return;
    const payload = normalizeAssetPayload(editForm);
    const validationErrors = validateAssetPayload(payload);
    if (validationErrors.length) {
      showNotification(validationErrors[0], "warning");
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiFetch(`/asset-tracker/entries/${editingEntryId}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(parseApiError(err, "Update failed"));
      }
      showNotification("Asset entry updated successfully", "success");
      closeEditModal();
      await loadEntries();
    } catch (err) {
      showNotification(err.message || "Could not update entry", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    const payload = normalizeAssetPayload(form);
    const validationErrors = validateAssetPayload(payload);
    if (validationErrors.length) {
      showNotification(validationErrors[0], "warning");
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiFetch("/asset-tracker/entries", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(parseApiError(err, "Submission failed"));
      }
      const created = await res.json();
      showNotification(
        `Asset ${created.entry_id || "entry"} registered successfully`,
        "success",
      );
      setForm(EMPTY_FORM);
      setModalOpen(false);
      await loadEntries();
    } catch (err) {
      showNotification(err.message || "Could not submit entry", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const buildBulkPayload = () => {
    const rows = bulkPreview.length
      ? bulkPreview
      : parseCsvText(bulkText).map((row) => {
          const department_id = resolveDepartmentId(row.department_ref, departments);
          return { ...row, department_id };
        });

    const errors = [];
    const entriesPayload = rows
      .map((row) => {
        if (!row.ip_address?.trim()) {
          errors.push(`Row ${row.row}: IP address is required`);
          return null;
        }
        if (!row.database_name?.trim()) {
          errors.push(`Row ${row.row}: Database name is required`);
          return null;
        }
        if (!row.department_id) {
          errors.push(
            `Row ${row.row}: Department "${row.department_ref}" not found`,
          );
          return null;
        }
        const entry = {
          ip_address: row.ip_address.trim(),
          database_name: row.database_name.trim(),
          department_id: row.department_id,
        };
        const entryId = row.entry_id?.trim();
        if (entryId) entry.entry_id = entryId;
        return entry;
      })
      .filter(Boolean);

    return { entriesPayload, errors };
  };

  const handleBulkPreview = () => {
    try {
      const parsed = parseCsvText(bulkText);
      const preview = parsed.map((row) => ({
        ...row,
        department_id: resolveDepartmentId(row.department_ref, departments),
      }));
      setBulkPreview(preview);
      setBulkErrors([]);
      showNotification(`Parsed ${preview.length} rows from CSV`, "success");
    } catch (err) {
      setBulkPreview([]);
      showNotification(err.message, "error");
    }
  };

  const handleBulkSubmit = async () => {
    const { entriesPayload, errors } = buildBulkPayload();
    if (errors.length) {
      setBulkErrors(errors);
      showNotification("Fix validation errors before uploading", "warning");
      return;
    }
    if (!entriesPayload.length) {
      showNotification("No valid rows to upload", "warning");
      return;
    }
    setSubmitting(true);
    setBulkErrors([]);
    try {
      const res = await apiFetch("/asset-tracker/entries/bulk/async", {
        method: "POST",
        body: JSON.stringify({ entries: entriesPayload }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(parseApiError(err, "Bulk upload failed"));
      }
      const { task_id: taskId } = await res.json();
      showNotification("Bulk upload queued — processing in background…", "info");

      const data = await pollBulkUploadTask(taskId);
      if (data.failed) {
        setBulkErrors(
          (data.errors || []).map(
            (item) => `Row ${item.row} (${item.entry_id}): ${item.detail}`,
          ),
        );
        showNotification(
          `${data.created} uploaded, ${data.failed} failed`,
          data.created ? "warning" : "error",
        );
      } else {
        showNotification(`${data.created} asset entries uploaded`, "success");
        setBulkText("");
        setBulkPreview([]);
        setModalOpen(false);
      }
      await loadEntries();
    } catch (err) {
      showNotification(err.message || "Bulk upload failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setBulkText(String(reader.result || ""));
      setBulkPreview([]);
      setBulkErrors([]);
    };
    reader.readAsText(file);
  };

  const handleDelete = async (id) => {
    try {
      const res = await apiFetch(`/asset-tracker/entries/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Delete failed");
      showNotification("Entry removed", "success");
      setSelectedIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      loadEntries();
    } catch {
      showNotification("Could not delete entry", "error");
    }
  };

  const downloadTemplate = () => {
    const csv =
      "ip,database_name,department_id\n192.168.1.10,customer_db,1\n10.0.0.22,analytics_prod,2";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "asset_tracker_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="asset-tracker relative flex flex-col -m-4 lg:-m-6 min-h-full">
      <style>{TABLE_SCROLL_CSS}</style>
      <div className="absolute inset-0 pointer-events-none">
        <PremiumBlueBg />
      </div>

      {/* White header */}
      <header className="relative shrink-0 bg-white border-b border-slate-200 px-4 sm:px-5 py-3.5 flex flex-wrap items-center justify-between gap-3">
        <span className={premiumTopLine} />
        <div className="flex items-center gap-3 min-w-0">
          <PageIcon icon={HardDrive} />
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-slate-900 leading-tight inline-flex items-center gap-1.5">
              Asset Tracker
              <HelpTooltip
                content="A register of systems that store personal data: their ID, IP address, database, and owning department."
                size={14}
                position="bottom"
                maxWidth={320}
              />
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Register assets by entry ID, IP, database, and department
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            variant="secondary"
            size="sm"
            title="Upload or paste many assets from a CSV file."
            onClick={() => openModal("bulk")}
            className="flex-1 sm:flex-none !rounded-lg !border-slate-200"
            leftIcon={<Upload size={14} />}
          >
            Bulk import
          </Button>
          <Button
            size="sm"
            title="Add one asset: IP address, database, and department."
            onClick={() => openModal("manual")}
            className="flex-1 sm:flex-none !rounded-lg !bg-gradient-to-r !from-blue-600 !to-indigo-600 hover:!from-blue-700 hover:!to-indigo-700 !border-0"
            leftIcon={<Plus size={14} />}
          >
            Register asset
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="relative flex-1 flex flex-col px-4 sm:px-5 pt-4 pb-4 space-y-4 max-w-[1400px] w-full mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            label="Total assets"
            value={stats.total}
            icon={Layers}
            gradient="from-indigo-500 to-violet-600"
            tooltip="Every system registered here: IP address, database, and the department that owns it."
          />
          <StatCard
            label="Active"
            value={stats.active}
            icon={Power}
            gradient="from-emerald-500 to-teal-600"
            tooltip="Assets that are currently in use and included in tracking."
          />
          <StatCard
            label="Inactive"
            value={stats.inactive}
            icon={PowerOff}
            gradient="from-slate-500 to-slate-600"
            tooltip="Assets that were turned off. They stay in the register for history and are not treated as live systems."
          />
          <StatCard
            label="Departments"
            value={stats.departments}
            icon={Building2}
            gradient="from-amber-500 to-orange-500"
            tooltip="How many departments have at least one asset in this register."
          />
        </div>

        {/* Entries table */}
        <div className="relative overflow-hidden rounded-xl bg-white border border-slate-200 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3.5 border-b border-slate-100 bg-white">
            <div className="flex items-center gap-2.5 min-w-0">
              <PageIcon icon={Server} />
              <div className="min-w-0">
                <h2 className="text-sm sm:text-base font-semibold text-slate-900 inline-flex items-center gap-1">
                  Submitted entries
                  <HelpTooltip
                    content="The inventory of assets already saved. Filter by how they were added and whether they are active."
                    size={13}
                    position="bottom"
                  />
                </h2>
                <p className="text-xs text-slate-500">Your registered asset inventory</p>
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  ref={searchInputRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  title="Search by entry ID, IP address, database, or department."
                  placeholder="Search entries…"
                  className="w-full sm:w-52 pl-9 pr-14 py-2 rounded-lg border border-slate-200 bg-slate-50/50 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:bg-white"
                />
                {!search && (
                  <kbd className="hidden sm:flex absolute right-2.5 top-1/2 -translate-y-1/2 items-center justify-center h-5 px-1.5 rounded-md border border-slate-200 bg-white text-[10px] font-semibold text-slate-400">
                    /
                  </kbd>
                )}
              </div>
              <button
                type="button"
                onClick={() => setFiltersOpen((v) => !v)}
                className={`relative p-2 rounded-lg border transition flex-shrink-0 ${
                  filtersOpen || hasActiveFilters
                    ? "border-blue-300 bg-blue-50 text-blue-600"
                    : "border-slate-200 bg-white text-slate-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50"
                }`}
                title="Filters"
              >
                <SlidersHorizontal size={15} />
                {hasActiveFilters && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-blue-600 ring-2 ring-white" />
                )}
              </button>
              <button
                type="button"
                onClick={handleExport}
                disabled={!totalEntries}
                className="p-2 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 transition flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Export CSV"
              >
                <FileDown size={15} />
              </button>
              <button
                type="button"
                onClick={loadEntries}
                className="p-2 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 transition flex-shrink-0"
                title="Refresh"
              >
                <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
              </button>
            </div>
          </div>

          {filtersOpen && (
            <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/60">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 mr-1">
                Source
                <HelpTooltip
                  content="Manual means someone typed the asset. Bulk means it was uploaded from a CSV file."
                  size={12}
                  position="bottom"
                />
              </span>
              <div className="flex rounded-lg bg-white border border-slate-200 p-0.5 gap-0.5">
                {[
                  { id: "all", label: "All" },
                  { id: "manual", label: "Manual" },
                  { id: "bulk", label: "Bulk" },
                ].map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setFilterSource(id);
                      setPage(1);
                    }}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold transition ${
                      filterSource === id
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 ml-2 mr-1">
                Status
                <HelpTooltip
                  content="Active assets are in use. Inactive assets are kept in the register but are not live."
                  size={12}
                  position="bottom"
                />
              </span>
              <div className="flex rounded-lg bg-white border border-slate-200 p-0.5 gap-0.5">
                {[
                  { id: "all", label: "All" },
                  { id: "active", label: "Active" },
                  { id: "inactive", label: "Inactive" },
                ].map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setFilterAssetStatus(id);
                      setPage(1);
                    }}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold transition ${
                      filterAssetStatus === id
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 ml-2 mr-1">
                Department
                <HelpTooltip content="Show assets owned by one department." size={12} position="bottom" />
              </span>
              <select
                value={filterDept}
                onChange={(e) => {
                  setFilterDept(e.target.value);
                  setPage(1);
                }}
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              >
                <option value="">All departments</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>

              <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 ml-2 mr-1">
                Rows
                <HelpTooltip content="How many assets to show on each page." size={12} position="bottom" />
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              >
                {[10, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-red-600 ml-auto"
                >
                  <XCircle size={13} /> Clear filters
                </button>
              )}
            </div>
          )}

          {selectedIds.size > 0 && (
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-blue-100 bg-blue-50/70">
              <span className="text-xs sm:text-sm font-semibold text-blue-800">
                {selectedIds.size} {selectedIds.size === 1 ? "entry" : "entries"} selected
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedIds(new Set())}
                  className="text-xs font-semibold text-slate-500 hover:text-slate-700"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-red-600 hover:bg-red-700 transition disabled:opacity-60"
                >
                  {bulkDeleting ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Trash2 size={13} />
                  )}
                  Delete selected
                </button>
              </div>
            </div>
          )}

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-slate-100/80">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-3 space-y-2.5 animate-pulse">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-slate-200" />
                    <div className="h-3.5 w-28 rounded bg-slate-200" />
                  </div>
                  <div className="pl-9 space-y-1.5">
                    <div className="h-2.5 w-40 rounded bg-slate-100" />
                    <div className="h-2.5 w-32 rounded bg-slate-100" />
                  </div>
                </div>
              ))
            ) : totalEntries === 0 && !hasActiveFilters && !search.trim() ? (
              <div className="py-14 px-4 text-center">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mx-auto mb-3">
                  <HardDrive size={22} className="text-blue-500" />
                </div>
                <p className="text-sm font-medium text-slate-600">No entries yet</p>
                <p className="text-xs text-slate-400 mt-1">
                  Click Register asset to add your first entry
                </p>
                <Button
                  size="sm"
                  onClick={() => openModal("manual")}
                  className="mt-4 !rounded-xl !bg-gradient-to-r !from-blue-600 !to-indigo-600 !border-0 !shadow-lg !shadow-blue-500/25"
                  leftIcon={<Plus size={14} />}
                >
                  Register asset
                </Button>
              </div>
            ) : entries.length === 0 ? (
              <div className="py-14 px-4 text-center">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mx-auto mb-3">
                  <SlidersHorizontal size={20} className="text-slate-400" />
                </div>
                <p className="text-sm font-medium text-slate-600">No matching entries</p>
                <p className="text-xs text-slate-400 mt-1">Try adjusting your search or filters</p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="mt-3 text-xs font-semibold text-blue-600 hover:text-blue-700"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              entries.map((row) => (
                <div
                  key={row.id}
                  className={`p-3 space-y-2 transition-colors ${
                    selectedIds.has(row.id) ? "bg-blue-50/70" : "hover:bg-blue-50/50"
                  } ${normalizeAssetStatus(row.status) === "inactive" ? "opacity-75" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <button
                        type="button"
                        onClick={() => toggleSelect(row.id)}
                        className="flex-shrink-0 text-slate-300 hover:text-blue-600"
                      >
                        {selectedIds.has(row.id) ? (
                          <CheckSquare size={16} className="text-blue-600" />
                        ) : (
                          <Square size={16} />
                        )}
                      </button>
                      <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Server size={13} className="text-blue-600" />
                      </div>
                      <p className="font-semibold text-slate-900 text-sm truncate">
                        {row.entry_id}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <AssetStatusBadge
                        status={row.status}
                        disabled={statusUpdatingId === row.id}
                        onToggle={() => handleToggleStatus(row)}
                      />
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          row.source === "bulk"
                            ? "bg-sky-100 text-sky-700 ring-1 ring-sky-200/60"
                            : "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200/60"
                        }`}
                      >
                        {row.source}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-slate-600 pl-16">
                    <span className="text-slate-400" title="Network address of the system that stores the data.">IP</span>
                    <span className="font-mono truncate flex items-center gap-1.5">
                      {row.ip_address}
                      <button
                        type="button"
                        onClick={() => handleCopyIp(row.ip_address)}
                        className="text-slate-300 hover:text-blue-600"
                      >
                        <Copy size={11} />
                      </button>
                    </span>
                    <span className="text-slate-400" title="Database or platform name.">Database</span>
                    <span className="truncate">{row.database_name || "—"}</span>
                    <span className="text-slate-400" title="The department that owns this asset.">Department</span>
                    <span className="truncate">{row.department_name || "—"}</span>
                    <span className="text-slate-400" title="When this asset was added to the register.">Submitted</span>
                    <span>{formatDate(row.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-2 pl-16">
                    <button
                      type="button"
                      onClick={() => handleToggleStatus(row)}
                      disabled={statusUpdatingId === row.id}
                      className={`inline-flex items-center gap-1 text-xs font-semibold transition disabled:opacity-50 ${
                        normalizeAssetStatus(row.status) === "active"
                          ? "text-amber-600 hover:text-amber-700"
                          : "text-emerald-600 hover:text-emerald-700"
                      }`}
                    >
                      {statusUpdatingId === row.id ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : normalizeAssetStatus(row.status) === "active" ? (
                        <PowerOff size={12} />
                      ) : (
                        <Power size={12} />
                      )}
                      {normalizeAssetStatus(row.status) === "active" ? "Deactivate" : "Activate"}
                    </button>
                    <button
                      type="button"
                      onClick={() => openEditModal(row)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(row.id)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop table */}
          <div
            className={`hidden md:block ${TABLE_SCROLL_CLASS} overflow-auto max-h-[min(520px,calc(100vh-20rem))]`}
          >
            <table className="w-full text-sm min-w-[1020px] border-separate border-spacing-0">
              <thead className="relative sticky top-0 z-20">
                <tr className={`text-left text-[11px] uppercase tracking-wider ${TABLE_HEAD_CLASS}`}>
                  <th
                    className="py-3 pl-4 pr-2 font-semibold w-10 sticky left-0 z-30"
                    style={{ background: TABLE_HEAD_GRADIENT }}
                  >
                    <button
                      type="button"
                      onClick={toggleSelectAllOnPage}
                      className="text-slate-500 hover:text-slate-800"
                    >
                      {entries.length > 0 && entries.every((r) => selectedIds.has(r.id)) ? (
                        <CheckSquare size={14} />
                      ) : (
                        <Square size={14} />
                      )}
                    </button>
                  </th>
                  {[
                    { field: "entry_id", label: "Entry ID", tip: "The ID assigned when this asset was registered." },
                    { field: "ip_address", label: "IP address", tip: "Network address of the system that stores the data." },
                    { field: "database_name", label: "Database", tip: "Database or platform name, chosen from Automated ROPA or typed in." },
                    { field: "department_name", label: "Department", tip: "The department that owns this asset." },
                    { field: "source", label: "Source", tip: "Manual if someone entered it. Bulk if it came from a CSV upload." },
                    { field: "status", label: "Status", tip: "Active means the asset is in use. Inactive means it was turned off." },
                    { field: "created_at", label: "Submitted", tip: "When this asset was added to the register." },
                  ].map(({ field, label, tip }) => (
                    <th
                      key={field}
                      className="py-3 px-3 font-semibold"
                      style={{ background: TABLE_HEAD_GRADIENT }}
                    >
                      <span className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => toggleSort(field)}
                          className="inline-flex items-center gap-1 hover:text-slate-900"
                        >
                          {label} <SortIcon field={field} />
                        </button>
                        <HelpTooltip content={tip} size={11} position="bottom" />
                      </span>
                    </th>
                  ))}
                  <th
                    className={`py-3 px-3 font-semibold text-center w-[170px] sticky right-0 z-30 shadow-[-4px_0_8px_-4px_rgba(15,23,42,0.08)] ${TABLE_HEAD_CLASS}`}
                    style={{ background: TABLE_HEAD_GRADIENT }}
                  >
                    <span className="inline-flex items-center gap-1">
                      Actions
                      <HelpTooltip content="Copy the IP, turn the asset on or off, edit it, or remove it." size={11} position="bottom" />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="border-t border-slate-100 animate-pulse">
                      <td className="py-3 pl-4 pr-2">
                        <div className="w-3.5 h-3.5 rounded bg-slate-200" />
                      </td>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j} className="py-3 px-3">
                          <div className="h-3.5 rounded bg-slate-100" style={{ width: `${60 + (j % 3) * 20}%` }} />
                        </td>
                      ))}
                      <td className="py-3 px-4" />
                    </tr>
                  ))
                ) : totalEntries === 0 && !hasActiveFilters && !search.trim() ? (
                  <tr>
                    <td colSpan={9} className="py-14 text-center">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mx-auto mb-3">
                        <HardDrive size={22} className="text-blue-500" />
                      </div>
                      <p className="text-sm font-medium text-slate-600">No entries yet</p>
                      <p className="text-xs text-slate-400 mt-1 mb-4">
                        Register assets manually or via bulk CSV import
                      </p>
                      <Button
                        size="sm"
                        onClick={() => openModal("manual")}
                        className="!rounded-xl !bg-gradient-to-r !from-blue-600 !to-indigo-600 !border-0 !shadow-lg !shadow-blue-500/25"
                        leftIcon={<Plus size={14} />}
                      >
                        Register asset
                      </Button>
                    </td>
                  </tr>
                ) : entries.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-14 text-center">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mx-auto mb-3">
                        <SlidersHorizontal size={20} className="text-slate-400" />
                      </div>
                      <p className="text-sm font-medium text-slate-600">No matching entries</p>
                      <p className="text-xs text-slate-400 mt-1 mb-3">
                        Try adjusting your search or filters
                      </p>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                        >
                          Clear filters
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  entries.map((row, index) => (
                    <tr
                      key={row.id}
                      className={`group border-t border-slate-100 transition-colors hover:bg-blue-50/40 ${
                        selectedIds.has(row.id)
                          ? "bg-blue-50/70"
                          : index % 2 === 1
                          ? "bg-slate-50/40"
                          : "bg-white"
                      } ${normalizeAssetStatus(row.status) === "inactive" ? "opacity-75" : ""}`}
                    >
                      <td className="py-3 pl-4 pr-2">
                        <button
                          type="button"
                          onClick={() => toggleSelect(row.id)}
                          className="text-slate-300 hover:text-blue-600"
                        >
                          {selectedIds.has(row.id) ? (
                            <CheckSquare size={15} className="text-blue-600" />
                          ) : (
                            <Square size={15} />
                          )}
                        </button>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                            <Server size={13} className="text-white" />
                          </div>
                          <span className="font-medium text-slate-900">{row.entry_id}</span>
                        </div>
                      </td>
                      <td className="py-3 px-3 font-mono text-xs text-slate-700">
                        <span className="inline-flex items-center gap-1.5 group">
                          {row.ip_address}
                          <button
                            type="button"
                            onClick={() => handleCopyIp(row.ip_address)}
                            className="text-slate-300 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition"
                            title="Copy IP address"
                          >
                            <Copy size={12} />
                          </button>
                        </span>
                      </td>
                      <td className="py-3 px-3 text-slate-700">{row.database_name || "—"}</td>
                      <td className="py-3 px-3">
                        <span className="inline-flex items-center gap-1 text-slate-700">
                          <Building2 size={12} className="text-slate-400" />
                          {row.department_name || "—"}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <span
                          className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            row.source === "bulk"
                              ? "bg-sky-100 text-sky-700 ring-1 ring-sky-200/60"
                              : "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200/60"
                          }`}
                        >
                          {row.source}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <AssetStatusBadge
                          status={row.status}
                          disabled={statusUpdatingId === row.id}
                          onToggle={() => handleToggleStatus(row)}
                        />
                      </td>
                      <td className="py-3 px-3 text-xs text-slate-500 whitespace-nowrap">
                        {formatDate(row.created_at)}
                      </td>
                      <td
                        className={`py-3 px-2 text-center w-[170px] sticky right-0 z-10 shadow-[-4px_0_8px_-4px_rgba(15,23,42,0.08)] group-hover:bg-blue-50/40 ${
                          selectedIds.has(row.id)
                            ? "bg-blue-50/70"
                            : index % 2 === 1
                            ? "bg-slate-50/40"
                            : "bg-white"
                        }`}
                      >
                        <div className="inline-flex items-center justify-center gap-0.5 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => handleToggleStatus(row)}
                            disabled={statusUpdatingId === row.id}
                            className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-50 ${
                              normalizeAssetStatus(row.status) === "active"
                                ? "text-amber-600 hover:bg-amber-50"
                                : "text-emerald-600 hover:bg-emerald-50"
                            }`}
                            title={
                              normalizeAssetStatus(row.status) === "active"
                                ? "Mark inactive"
                                : "Mark active"
                            }
                          >
                            {statusUpdatingId === row.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : normalizeAssetStatus(row.status) === "active" ? (
                              <PowerOff size={12} />
                            ) : (
                              <Power size={12} />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditModal(row)}
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold text-blue-600 hover:bg-blue-50 transition"
                            title="Edit entry"
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(row.id)}
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 transition"
                            title="Remove entry"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          {!loading && totalEntries > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50/40">
              <p className="text-xs text-slate-500">
                Showing{" "}
                <span className="font-semibold text-slate-700">
                  {(currentPage - 1) * pageSize + 1}
                </span>
                –
                <span className="font-semibold text-slate-700">
                  {Math.min(currentPage * pageSize, totalEntries)}
                </span>{" "}
                of <span className="font-semibold text-slate-700">{totalEntries}</span> entries
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-blue-600 hover:border-blue-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs font-medium text-slate-600 px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-blue-600 hover:border-blue-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <RegisterAssetModal
        open={modalOpen}
        onClose={closeModal}
        mode={mode}
        setMode={setMode}
        form={form}
        setField={setField}
        departments={departments}
        departmentsLoading={departmentsLoading}
        ropaDatabases={ropaDatabases}
        ropaDatabasesLoading={ropaDatabasesLoading}
        submitting={submitting}
        onManualSubmit={handleManualSubmit}
        bulkText={bulkText}
        setBulkText={setBulkText}
        bulkPreview={bulkPreview}
        setBulkPreview={setBulkPreview}
        bulkErrors={bulkErrors}
        setBulkErrors={setBulkErrors}
        onBulkPreview={handleBulkPreview}
        onBulkSubmit={handleBulkSubmit}
        onFileUpload={handleFileUpload}
        downloadTemplate={downloadTemplate}
        fileRef={fileRef}
      />

      <EditAssetModal
        open={editModalOpen}
        onClose={closeEditModal}
        form={editForm}
        setField={setEditField}
        departments={departments}
        departmentsLoading={departmentsLoading}
        ropaDatabases={ropaDatabases}
        ropaDatabasesLoading={ropaDatabasesLoading}
        submitting={submitting}
        onSubmit={handleEditSubmit}
      />
    </div>
  );
}